// PackageDetailDrawer.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, Divider, List, ListItem, ListItemText,
  CircularProgress, Toolbar, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, Link, Stack, Button, Chip, IconButton, TableSortLabel,
  useTheme, useMediaQuery,
} from '@mui/material';
import { ExpandMore, Close } from '@mui/icons-material';
import axios from 'axios';
import { formatDateTime, formatMoney, formatMonthNameYear, formatNumber, formatPercent, formatQty } from '../../utils/format';

const API = process.env.REACT_APP_API_BASE_URL;
// Hausform statt `Intl.NumberFormat('en-GB')` (Regel-16-Gate B1, 2026-10-06).
const fmtEUR = (v) => formatMoney(v);
const isNumericRef = (ref) => /^\d+$/.test(ref || '');

const eurDash = (v) => {
  const n = Number(v ?? 0);
  return Math.abs(n) < 1e-9 ? '–' : fmtEUR(n);
};

// ---------- Expandable per-month table (top-level overview + nested full-width detail) ----------

function monthShortLabel(ym) {
  return formatMonthNameYear(`${ym}-01T00:00:00Z`);
}

const num = (v) => Number(v ?? 0);
const eur = (v) => fmtEUR(v);

function cmpDesc(a, b, key) {
  const va = a?.[key]; const vb = b?.[key];
  const na = Number(va); const nb = Number(vb);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return nb - na;
  return String(vb ?? '').localeCompare(String(va ?? ''), undefined, { numeric: true });
}
const getComparator = (order, key) =>
  order === 'desc' ? (a, b) => cmpDesc(a, b, key) : (a, b) => -cmpDesc(a, b, key);

const HIDE_MOB = { display: { xs: 'none', sm: 'table-cell' } };

function ExpandableMonthBreakdown({ perMonth = [], refsByMonth = [], onOpenPackage }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Months (newest first)
  const months = useMemo(
    () => Array.from(new Set(perMonth.map((r) => r.month))).sort((a, b) => b.localeCompare(a)),
    [perMonth]
  );

  // Top-level sorting (month rows)
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('month'); // newest first
  const onSortHead = (key) => {
    setOrderBy((prev) => {
      if (prev === key) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else setOrder('asc');
      return key;
    });
  };

  // Details per month & sorting
  const detailsByMonth = useMemo(() => {
    const m = new Map();
    for (const r of refsByMonth) {
      const k = r.month;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return m;
  }, [refsByMonth]);

  const [expanded, setExpanded] = useState(() => new Set(months.length ? [months[0]] : [])); // default: newest open
  const [orderByPer, setOrderByPer] = useState({});
  const [orderPer, setOrderPer] = useState({}); // 'asc' | 'desc'
  const onSortDetail = (m, key) => {
    setOrderByPer((prev) => ({ ...prev, [m]: key }));
    setOrderPer((prev) => {
      const cur = prev[m] || 'asc';
      const same = (orderByPer[m] === key);
      return { ...prev, [m]: same ? (cur === 'asc' ? 'desc' : 'asc') : 'asc' };
    });
  };
  const toggle = (m) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(m) ? next.delete(m) : next.add(m);
      return next;
    });

  // Build the month overview rows from correct alloc_* fields
  const monthRows = useMemo(() => {
    const map = new Map(perMonth.map((x) => [x.month, x]));
    return months.map((m) => {
      const r = map.get(m) || {};
      const qty  = num(r.qty);
      const rev  = num(r.revenue_net);
      const ful  = num(r.alloc_ful_cost);         // correct field
      const ship = num(r.alloc_ship_cost);        // correct field
      const stor = num(r.alloc_storage_cost);     // correct field
      const total = ful + ship + stor;            // compute instead of relying on r.total
      const avg  = qty > 0 ? total / qty : 0;     // <-- NEW
      return { month: m, qty, revenue_net: rev, fulfillment: ful, shipping: ship, storage: stor, total, avg_per_unit: avg };
    });
  }, [months, perMonth]);

  const sortedTop = useMemo(
    () => [...monthRows].sort(getComparator(order, orderBy)),
    [monthRows, order, orderBy]
  );

  if (!months.length) return null;

  // Header columns (top level): [expander] Month | Qty | Revenue | Fulfillment | Shipping | Storage | Total
  const COLSPAN = 9; // total columns including the expander cell (added Avg col)

  return (
    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
    <Paper variant="outlined" sx={{ minWidth: isMobile ? 'unset' : 600 }}>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell>
              <TableSortLabel active={orderBy === 'month'} direction={order} onClick={() => onSortHead('month')}>
                Month
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel active={orderBy === 'qty'} direction={order} onClick={() => onSortHead('qty')}>
                Qty
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel active={orderBy === 'revenue_net'} direction={order} onClick={() => onSortHead('revenue_net')}>
                Revenue €
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={HIDE_MOB}>
              <TableSortLabel active={orderBy === 'fulfillment'} direction={order} onClick={() => onSortHead('fulfillment')}>
                Fulfillment €
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={HIDE_MOB}>
              <TableSortLabel active={orderBy === 'shipping'} direction={order} onClick={() => onSortHead('shipping')}>
                Shipping €
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={HIDE_MOB}>
              <TableSortLabel active={orderBy === 'storage'} direction={order} onClick={() => onSortHead('storage')}>
                Storage €
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel active={orderBy === 'total'} direction={order} onClick={() => onSortHead('total')}>
                <strong>Total €</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel active={orderBy === 'avg_per_unit'} direction={order} onClick={() => onSortHead('avg_per_unit')}>
                Avg/item €
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedTop.map((t) => {
            const m = t.month;

            // detail rows for this month, mapped to same columns; keep reference_number for click-through
            const rawDetail = (detailsByMonth.get(m) || []);
            const detailRows = rawDetail.map((r) => {
              const qty  = num(r.qty);
              const rev  = num(r.revenue_net);
              const ful  = num(r.warehouse_total);     // sum of warehouse buckets
              const ship = num(r.transport_total);
              const stor = num(r.storage_total);       // may be 0
              const total = num(r.total ?? (ful + ship + stor));
              return {
                reference_number: r.reference_number, // click-through
                month: m, qty, revenue_net: rev, fulfillment: ful, shipping: ship, storage: stor, total,
              };
            });

            const ob = orderByPer[m] || 'total';
            const od = orderPer[m] || 'desc';
            const sortedDetail = [...detailRows].sort(getComparator(od, ob));

            return (
              <React.Fragment key={`m-${m}`}>
                {/* Month overview (click to expand) */}
                <TableRow
                  hover
                  onClick={() => toggle(m)}
                  sx={{ backgroundColor: 'action.hover', cursor: 'pointer' }}
                >
                  <TableCell width={56}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggle(m); }}
                      aria-label={`${expanded.has(m) ? 'Collapse' : 'Expand'} ${monthShortLabel(m)}`}>
                      <ExpandMore
                        sx={{ transition: 'transform 0.2s', transform: expanded.has(m) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </IconButton>
                  </TableCell>
                  <TableCell>{monthShortLabel(m)}</TableCell>
                  <TableCell align="right">{formatQty(t.qty)}</TableCell>
                  <TableCell align="right">{eur(t.revenue_net)}</TableCell>
                  <TableCell align="right" sx={HIDE_MOB}>{eur(t.fulfillment)}</TableCell>
                  <TableCell align="right" sx={HIDE_MOB}>{eur(t.shipping)}</TableCell>
                  <TableCell align="right" sx={HIDE_MOB}>{eur(t.storage)}</TableCell>
                  <TableCell align="right"><strong>{eur(t.total)}</strong></TableCell>
                  <TableCell align="right"><strong>{eur(t.avg_per_unit)}</strong></TableCell>
                </TableRow>

                {/* Nested full-width detail table */}
                {expanded.has(m) && sortedDetail.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={COLSPAN} sx={{ p: 0 }}>
                      <Table size="small" sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'background.default' }}>
                            <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Reference</TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={ob === 'qty'} direction={od} onClick={() => onSortDetail(m, 'qty')}>
                                Qty
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={ob === 'revenue_net'} direction={od} onClick={() => onSortDetail(m, 'revenue_net')}>
                                Revenue €
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={HIDE_MOB}>
                              <TableSortLabel active={ob === 'fulfillment'} direction={od} onClick={() => onSortDetail(m, 'fulfillment')}>
                                Fulfillment €
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={HIDE_MOB}>
                              <TableSortLabel active={ob === 'shipping'} direction={od} onClick={() => onSortDetail(m, 'shipping')}>
                                Shipping €
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={ob === 'total'} direction={od} onClick={() => onSortDetail(m, 'total')}>
                                <strong>Total €</strong>
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedDetail.map((r, idx) => (
                            <TableRow
                              hover
                              key={`${m}-${r.reference_number || idx}`}
                              sx={{ cursor: r.reference_number ? 'pointer' : 'default' }}
                              onClick={() => r.reference_number && onOpenPackage?.(r.reference_number)}
                            >
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {r.reference_number || monthShortLabel(m)}
                              </TableCell>
                              <TableCell align="right">{formatQty(r.qty)}</TableCell>
                              <TableCell align="right">{eurDash(r.revenue_net)}</TableCell>
                              <TableCell align="right" sx={HIDE_MOB}>{eurDash(r.fulfillment)}</TableCell>
                              <TableCell align="right" sx={HIDE_MOB}>{eurDash(r.shipping)}</TableCell>
                              <TableCell align="right"><strong>{eurDash(r.total)}</strong></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
    </Box>
  );
}


/**
 * Props:
 *  - referenceNumber?: string (DN/RT/IN/TO/SKU)
 *  - sku?: string       (to open the "per-SKU aggregation" drawer)
 *  - mode?: 'package' | 'sku' (optional; inferred from props if omitted)
 *  - onClose: () => void
 *  - onOpenPackage?: (ref: string) => void    (open another package from drawer)
 */
export default function PackageDetailDrawer({
  referenceNumber,
  sku,
  mode,
  onClose,
  onOpenPackage = () => {},
}) {
  // Decide active mode
  const activeMode = useMemo(() => {
    if (mode) return mode;
    if (sku) return 'sku';
    return 'package';
  }, [mode, sku]);

  // Normalize ref for package mode
  const ref =
    typeof referenceNumber === 'string'
      ? referenceNumber
      : (referenceNumber && referenceNumber.reference_number) || '';

  // Local state
  const [loading, setLoading] = useState(false);
  const [pkgData, setPkgData] = useState(null);
  const [skuData, setSkuData] = useState(null);

  // Fetch (package mode)
  useEffect(() => {
    if (activeMode !== 'package') return;
    if (!ref) { setPkgData(null); return; }
    setLoading(true);
    axios.get(`${API}/api/fulfillment/package/${encodeURIComponent(ref)}`)
      .then(res => setPkgData(res.data))
      .catch(() => setPkgData(null))
      .finally(() => setLoading(false));
  }, [activeMode, ref]);

  // Fetch (sku mode)
  useEffect(() => {
    if (activeMode !== 'sku') return;
    if (!sku) { setSkuData(null); return; }
    setLoading(true);
    axios.get(`${API}/api/fulfillment/allocation/sku/${encodeURIComponent(sku)}`)
      .then(res => setSkuData(res.data))
      .catch(() => setSkuData(null))
      .finally(() => setLoading(false));
  }, [activeMode, sku]);

  // ================= RENDER =================
  if (activeMode === 'sku') {
    return (
      <Drawer
        anchor="right"
        open={!!sku}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 1000 } } }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Product Allocation (SKU)</Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Toolbar>
        <Divider />
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : skuData ? (
            <>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>
                  {skuData.product_meta?.product_name || 'Product'}
                  {skuData.product_meta?.variant_name ? ` — ${skuData.product_meta.variant_name}` : ''}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                SKU: {skuData.product_meta?.sku || skuData.sku}
                {skuData.product_link && (
                  <>
                    {' · '}
                    <Link href={skuData.product_link} underline="hover">
                      Open product
                    </Link>
                  </>
                )}
                {skuData.product_meta?.vendor ? ` · Vendor: ${skuData.product_meta.vendor}` : ''}
              </Typography>

              {/* KPIs (sum of per_month) + new averages */}
              {Array.isArray(skuData.per_month) && skuData.per_month.length > 0 && (
                <Paper variant="outlined" sx={{ my: 2, p: { xs: 1.5, sm: 2 } }}>
                  {(() => {
                    const sum = skuData.per_month.reduce((acc, m) => {
                      acc.qty += Number(m.qty || 0);
                      acc.rev += Number(m.revenue_net || 0);
                      acc.ful += Number(m.alloc_ful_cost || 0);
                      acc.ship += Number(m.alloc_ship_cost || 0);
                      acc.stor += Number(m.alloc_storage_cost || 0);
                      return acc;
                    }, { qty: 0, rev: 0, ful: 0, ship: 0, stor: 0 });
                    const total = sum.ful + sum.ship + sum.stor;
                    const avgCostPerItem = sum.qty > 0 ? total / sum.qty : 0;
                    const avgSharePct = sum.rev > 0 ? (total / sum.rev) * 100 : 0;

                    return (
                      <Stack direction="row" spacing={{ xs: 2, sm: 3 }} useFlexGap flexWrap="wrap" sx={{ rowGap: { xs: 1.5, sm: 2 } }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total qty</Typography>
                          <Typography variant="h6">{Math.round(sum.qty)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total revenue</Typography>
                          <Typography variant="h6">{fmtEUR(sum.rev)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Fulfillment</Typography>
                          <Typography variant="h6">{fmtEUR(sum.ful)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Shipping</Typography>
                          <Typography variant="h6">{fmtEUR(sum.ship)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Storage</Typography>
                          <Typography variant="h6">{fmtEUR(sum.stor)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                          <Typography variant="h6">{fmtEUR(total)}</Typography>
                        </Box>
                        {/* NEW: Average cost/item */}
                        <Box>
                          <Typography variant="caption" color="text.secondary">Avg cost/item</Typography>
                          <Typography variant="h6">{fmtEUR(avgCostPerItem)}</Typography>
                        </Box>
                        {/* NEW: Average share of order */}
                        <Box>
                          <Typography variant="caption" color="text.secondary">Avg share of order</Typography>
                          <Typography variant="h6">
                            {Number.isFinite(avgSharePct) ? formatPercent(avgSharePct / 100, { digits: 1 }) : '–'}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })()}
                </Paper>
              )}

              {/* Consolidated expandable month table */}
              {skuData?.per_month?.length > 0 && (
                <ExpandableMonthBreakdown
                  perMonth={skuData.per_month}
                  refsByMonth={skuData.references_by_month || []}
                  onOpenPackage={onOpenPackage}
                />
              )}
            </>
          ) : (
            <Typography>No data available</Typography>
          )}
        </Box>
      </Drawer>
    );
  }

  // ======== RENDER: PACKAGE drawer (existing behavior) ========
  const data = pkgData;
  const numericRef = isNumericRef(data?.reference_number || ref);
  const breakdownRows = [
    ['picking_net', 'Picking €'],
    ['packing_net', 'Packing €'],
    ['loading_net', 'Loading €'],
    ['materials_net', 'Materials €'],
    ['fees_net', 'Fees €'],
    ['returns_net', 'Returns €'],
    ['storage_net', 'Storage €'],
    ['other_net', 'Other €'],
    ['transport_net', 'Transport €'],
  ];
  const showStorageChip = numericRef && (Number(data?.storage_net || 0) !== 0);

  return (
    <Drawer
      anchor="right"
      open={!!ref}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Typography variant="h6">Package Details</Typography>
        <IconButton edge="end" onClick={onClose} aria-label="close">
          <Close />
        </IconButton>
      </Toolbar>
      <Divider />
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <>
            {/* Header */}
            {numericRef && data.product_meta ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    {data.product_meta.product_name || 'Product'}
                    {data.product_meta.variant_name ? ` — ${data.product_meta.variant_name}` : ''}
                  </Typography>
                  {showStorageChip && (
                    <Chip size="small" color="default" label="Tagged as [STORAGE]" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  SKU: {data.product_meta.sku || data.reference_number}
                  {data.product_link && (
                    <>
                      {' · '}
                      <Link href={data.product_link} underline="hover">
                        Open product
                      </Link>
                    </>
                  )}
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6">{data.reference_number}</Typography>
                </Box>
              </>
            )}

            {/* Meta (DN/RT packages only) */}
            {!numericRef && (
              <>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                  Order: {data.order_number || 'N/A'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Carrier: {data.carrier || 'N/A'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Ship-to country: {data.order_country || 'N/A'}
                  {data.order_city ? ` · City: ${data.order_city}` : ''}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order total: {fmtEUR(data.order_total_paid)}
                </Typography>
              </>
            )}

            {/* Numbers */}
            {numericRef ? (
              <Paper variant="outlined" sx={{ my: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Storage total</Typography>
                <Typography variant="h6">{fmtEUR(data.storage_net)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Allocatable storage cost for this SKU.
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ my: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Component</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdownRows.map(([key, label]) => (
                      <TableRow key={key}>
                        <TableCell>{label}</TableCell>
                        <TableCell align="right">{fmtEUR(data[key])}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>{fmtEUR(data.total_net)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />

            {/* --- Monthly breakdown (package) --- */}
            {Array.isArray(data.month_breakdown) && data.month_breakdown.length > 0 && (
              <Paper variant="outlined" sx={{ my: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      {numericRef ? (
                        <TableCell align="right">Storage €</TableCell>
                      ) : (
                        <>
                          <TableCell align="right">Warehouse €</TableCell>
                          <TableCell align="right">Transport €</TableCell>
                          <TableCell align="right"><strong>Total €</strong></TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.month_breakdown.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell>
                          {monthShortLabel(m.month)}
                        </TableCell>
                        {numericRef ? (
                          <TableCell align="right">{fmtEUR(m.storage_net)}</TableCell>
                        ) : (
                          <>
                            <TableCell align="right">{fmtEUR(m.warehouse_net)}</TableCell>
                            <TableCell align="right">{fmtEUR(m.transport_net)}</TableCell>
                            <TableCell align="right"><strong>{fmtEUR(m.total_net)}</strong></TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {/* Items allocation by month (package-specific, if present) */}
            {Array.isArray(data?.items_by_month) && data.items_by_month.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" gutterBottom>Items allocation by month</Typography>
                <Table size="small" sx={{ mb: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Revenue €</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items_by_month.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell>{m.month}</TableCell>
                        <TableCell align="right">{formatNumber(m.qty ?? 0)}</TableCell>
                        <TableCell align="right">{fmtEUR(m.revenue_net)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell><strong>Totals</strong></TableCell>
                      <TableCell align="right">
                        <strong>{formatNumber(data.items_by_month.reduce((s, x) => s + (x.qty || 0), 0))}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{fmtEUR(data.items_by_month.reduce((s, x) => s + (x.revenue_net || 0), 0))}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary">
                  Quantities are proportionally allocated across months by this DN’s monthly cost share.
                </Typography>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Dates / Invoices */}
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Date Range"
                  secondary={
                    `${data.first_date
                      ? formatDateTime(data.first_date)
                      : '—'} → ${data.last_date
                      ? formatDateTime(data.last_date)
                      : '—'}`
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Invoices"
                  secondary={Array.isArray(data.invoices)
                    ? data.invoices.join(', ')
                    : (data.invoices || '—')}
                />
              </ListItem>
            </List>

            {/* Products in this shipment (DN/RT only) */}
            {!numericRef && Array.isArray(data.items) && data.items.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Products in this shipment
                </Typography>
                <Stack spacing={1.5}>
                  {data.items.map((it) => {
                    const name = it.product_name || it.variant_name || it.sku;
                    const link = `/products/${it.sku}`;
                    return (
                      <Box
                        key={`${it.sku}-${it.line_item_id || it.qty || Math.random()}`}
                        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Link href={link} underline="hover" sx={{ display: 'inline-block', wordBreak: 'break-all' }}>
                            {name}
                          </Link>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            SKU: {it.sku}{it.vendor ? ` · Vendor: ${it.vendor}` : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography variant="body2">Qty: {it.qty}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Revenue: {fmtEUR(it.revenue_net)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}

            {/* Related shipments (same order) */}
            {Array.isArray(data.siblings) && data.siblings.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Other shipments for order #{data.order_number}:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {data.siblings.map((r) => (
                    <Button
                      key={r}
                      size="small"
                      variant="text"
                      onClick={() => onOpenPackage(r)}
                    >
                      {r}
                    </Button>
                  ))}
                </Stack>
              </>
            )}
          </>
        ) : (
          <Typography>No data available</Typography>
        )}
      </Box>
    </Drawer>
  );
}
